import { Card, CardActions } from "@progress/kendo-react-layout";
import { ExternalDropZone, Upload, UploadFileInfo, UploadOnStatusChangeEvent } from "@progress/kendo-react-upload";
import { useFastaStore } from "@presentation/stores/FastaStore";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Button } from "@progress/kendo-react-buttons";
import { useInjection } from "@presentation/components/dependencyInjection"; //if you want to break the whole project with an error just at runtime(builds normally) impossible to guess just remove the /dependencyInjection
import React from "react";
import { useNotifications } from "@presentation/components/notifications";
import { UploadSettingsService } from "@application/useCases/softwareSettings/UploadSettingsService";
import { SoftwareSettingsMetadata, SettingsTypeEnumSchema, SoftwareEnumSchema } from "@domain/schemas/BI";
import { getSettingsSection, getSoftwareAccent } from "@domain/software";
import { useJobSetupStore } from "@presentation/stores/JobSetupStore";
import { useShouldShowEngineName } from "./useLicensedSoftware";

/**
 * Define the interface for the event parameter in onStatusChange
 */
interface StatusChangeEvent {
  status: number;
  data?: { message: string };
  response?: { response: { data: { message: string } } };
}

interface UploadSettingsProperties {
  onUploadSuccess?: (uploadedSettings: SoftwareSettingsMetadata) => void;
  settingsType: SettingsTypeEnumSchema;
}

const uploadReference = React.createRef<Upload>();

/**
 * Trailing part of the upload banner: either the engine the file is saved for, or
 * a note that the settings type is shared. Kept as its own component so the banner
 * does not need nested ternaries to express the three outcomes.
 */
const UploadTargetScope: React.FC<{ isSoftwareScoped: boolean; showEngineName: boolean; software?: SoftwareEnumSchema | null }> = ({ isSoftwareScoped, showEngineName, software }) => {
  if (!isSoftwareScoped) {
    return <> — shared across search engines</>;
  }

  if (showEngineName) {
    return (
      <>
        {" for "}
        <strong>{software ?? "Spectronaut"}</strong>
      </>
    );
  }

  return null;
};

// UploadCard component with TypeScript
const UploadSettings: React.FC<UploadSettingsProperties> = ({ onUploadSuccess, settingsType }) => {
  const uploadSettingsService = useInjection(UploadSettingsService);
  const { pushNotification } = useNotifications();
  const softwareVersion = useJobSetupStore((state) => state.CreateJobState.softwareVersion);
  const software = useJobSetupStore((state) => state.CreateJobState.software);

  const uploadVisibilitiy = useFastaStore((state) => state.isUploadVisible);
  const setUploadVisibilitiy = useFastaStore((state) => state.setUploadVisibilitiy);
  const uploadResults = useFastaStore((state) => state.uploadResults);
  const setUploadResults = useFastaStore((state) => state.setUploadResults);

  const settingsSection = getSettingsSection(software, settingsType);
  const settingsLabel = settingsSection?.label ?? settingsType;
  const isSoftwareScoped = settingsSection?.softwareScoped ?? true;
  const showEngineName = useShouldShowEngineName();
  // Shared types stay neutral so the banner does not imply an engine.
  const uploadTargetAccent = isSoftwareScoped ? getSoftwareAccent(software) : { background: "#f1f5f9", border: "#cbd5e1", text: "#475569" };

  const onSaveUrl = async (
    files: UploadFileInfo[],
    _options: { formData: FormData; requestOptions: unknown },
    onProgress: (uid: string, event: ProgressEvent<EventTarget>) => void,
  ): Promise<{ uid: string }> => {
    if (files.length > 0 && files[0].getRawFile) {
      const file = files[0].getRawFile();
      if (file) {
        if (!softwareVersion) {
          const softwareLabel = software ?? "Spectronaut";
          pushNotification(showEngineName ? `Select a ${softwareLabel} version before uploading settings.` : "Select a version before uploading settings.", "warning", 10_000);
          return { uid: "" };
        }

        // Call the upload service and pass the onProgress callback
        try {
          const response = await uploadSettingsService.uploadSettingsAsync(
            file,
            softwareVersion,
            (progressValue) => {
              // Update the progress as the file uploads
              onProgress(files[0].uid, {
                lengthComputable: true,
                loaded: progressValue,
                total: 100,
              } as ProgressEvent<EventTarget>);
            },
            settingsType,
            software,
          );

          // Once the upload is complete
          if (onUploadSuccess) {
            pushNotification("The settings file was successfuly uploaded.", "success", 10_000);
            onUploadSuccess(response as unknown as SoftwareSettingsMetadata);
          }
        } catch (error: any) {
          pushNotification(error.response?.data?.message || "An error occurred while uploading the file.", "error", 10_000);
        }
      } else {
        console.log(new Error("File could not be retrieved from UploadFileInfo."));
      }
    } else {
      console.log(new Error("No files to upload."));
    }
    return { uid: "" };
  };

  const onStatusChange = (event: UploadOnStatusChangeEvent) => {
    const statusEvent = event as unknown as StatusChangeEvent;

    if (statusEvent.status === 200 && statusEvent.data) {
      setUploadResults(statusEvent.data.message);
      setUploadVisibilitiy(true);
    } else if (statusEvent.response) {
      setUploadResults(statusEvent.response.response.data.message);
      alert(uploadResults);
    }
  };

  const toggleDialog = () => {
    setUploadVisibilitiy(false);
    location.assign("/");
  };

  return (
    <div>
      {uploadVisibilitiy && (
        <Dialog title="Upload Results" onClose={toggleDialog} width={"100%"} height={"100%"}>
          <p>Results of upload:</p>
          <p>{uploadResults}</p>

          <DialogActionsBar>
            <Button onClick={toggleDialog}>OK</Button>
          </DialogActionsBar>
        </Dialog>
      )}
      <Card style={{ height: "100%" }}>
        <CardActions>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr",
              fontSize: "14px",
              gridTemplateRows: "auto auto auto",
            }}
          >
            <br></br>
            {/*
              Upload is the moment the file's settings type, and for engine-scoped
              types its search engine, are decided. Restate both here so the choice
              is never made by accident from a form the user scrolled away from.
            */}
            <div
              role="note"
              style={{
                marginBottom: "10px",
                padding: "8px 10px",
                borderRadius: "6px",
                border: `1px solid ${uploadTargetAccent.border}`,
                backgroundColor: uploadTargetAccent.background,
                color: uploadTargetAccent.text,
                fontSize: "13px",
              }}
            >
              Uploading <strong>{settingsLabel}</strong>
              <UploadTargetScope isSoftwareScoped={isSoftwareScoped} showEngineName={showEngineName} software={software} />.
            </div>
            <div>
              <ExternalDropZone style={{ minWidth: "35vh" }} uploadRef={uploadReference} />
              <Upload
                ref={uploadReference}
                batch={false}
                multiple={false}
                withCredentials={false} //somehow cors doesnt work if set to true
                defaultFiles={[]}
                // restrictions={{
                //   allowedExtensions: [".prop"],
                //   maxFileSize: 5 * 1024 * 1024, // 5MB in bytes
                // }}
                saveUrl={onSaveUrl}
                onStatusChange={(event) => onStatusChange(event)}
                saveField="files"
              />
            </div>
          </div>
        </CardActions>
      </Card>
    </div>
  );
};

export default UploadSettings;
